import React, { useEffect, useRef, useState } from 'react';
import { fetchApi } from '../api/client';
import { CartDrawer } from '../commerce/CartDrawer';
import { QuickView } from '../commerce/QuickView';
import { SearchPanel } from '../commerce/SearchPanel';
import { useDocumentHead } from './useDocumentHead';
import { isThemePreview, previewThemeSlug } from '../api/previewVisit';
import { readBlock, type BlockDto } from '@xshop/shared';
import type { ElementTreeDto, ThemeStylesDto } from '@xshop/shared';
import { isThemeEditor } from '../editor/editorVisit';
import { useElementMarks } from '../editor/useElementMarks';
import { FORM_RENDERERS } from '../elements/forms';
import { ElementTreeView } from '../elements/render/ElementTree';
import { ProductSlotsProvider } from '../elements/render/productSlots';
import { elementVars } from '../elements/render/elementVars';
import '../elements/render/elements.css';
import type { StoreFacts } from '../elements/render/storeFacts';
import type { StorefrontThemePage } from '../themes/storefrontTheme';
import { themeOptionMarks } from '../elements/render/themeOptionMarks';
import { useThemeFonts } from './useThemeFonts';

// A page as the tree the merchant built. There is no second way to draw one any more: a theme ships a
// starter tree and the merchant edits it, so every page that reaches here is already the same shape.

/** The page to draw, and the store's palette it draws with. */
type ElementPageProps = {
  tree: ElementTreeDto;
  config: StorefrontThemePage;
  /** What a bound prop reads: the shop's name, what the cart holds, the product this page is about. */
  store: StoreFacts;
  motionPreview?: boolean;
};

// Draws a page: the store's tokens around the tree, and the marks the builder selects its nodes by.
export const ElementPage: React.FC<ElementPageProps> = ({ tree, config, store, motionPreview }) => {
  const editing = isThemeEditor();
  const marks = useElementMarks(editing);
  const blockId = isThemePreview() ? new URLSearchParams(window.location.search).get('blockPreview') : null;
  const [block, setBlock] = useState<BlockDto | null>(null);
  const [blockError, setBlockError] = useState(false);
  const preview = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!blockId) return;
    let cancelled = false;
    fetchApi<unknown[]>(`/blocks?theme=${encodeURIComponent(previewThemeSlug(window.location.search) ?? '')}&limit=100`)
      .then((rows) => {
        if (cancelled) return;
        const found = rows.map(readBlock).find((row) => row?.id === blockId);
        setBlock(found ?? null); setBlockError(!found);
      }).catch(() => { if (!cancelled) setBlockError(true); });
    return () => { cancelled = true; };
  }, [blockId]);
  useEffect(() => {
    if (!blockId || !preview.current) return;
    const report = () => window.parent.postMessage({ type: 'xshop:block-preview-size', height: preview.current?.scrollHeight ?? 0 }, '*');
    const observer = new ResizeObserver(report);
    observer.observe(preview.current); report();
    return () => observer.disconnect();
  }, [blockId, block]);
  useDocumentHead(store ?? null, config.page);
  useThemeFonts(config.styles as ThemeStylesDto | undefined);
  if (blockId && !block) return <p>{blockError ? 'This section preview could not be loaded.' : 'Loading section…'}</p>;

  return (
    // The store's tokens, so a node styled `{ token: 'primary' }` resolves against the shop's own palette.
    <div ref={preview} className="xe-scope" style={elementVars(config.styles as ThemeStylesDto)} {...themeOptionMarks(config.styles as ThemeStylesDto)}>
      <ProductSlotsProvider>
      <ElementTreeView
        tree={blockId && block ? { version: 2, roots: [block.node] } : tree}
        theme={config.styles as ThemeStylesDto}
        renderers={FORM_RENDERERS}
        editing={editing}
        motionPreview={motionPreview}
        marks={marks}
        store={store}
      />
      </ProductSlotsProvider>
      {/* Inside the scope, so the cart wears the theme's colours and fonts rather than the shell's. */}
      {blockId ? null : <><CartDrawer /><QuickView /><SearchPanel /></>}
    </div>
  );
};
